import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import api from '../utils/api';
import { 
  GripVertical, Save, RotateCcw, ChevronRight, ChevronDown, Plus, Minus,
  ArrowUp, ArrowDown, Search,
  LayoutDashboard, Calendar, FileText, Building, Users, Star, BarChart3,
  TrendingUp, HardDrive, Key, UserCheck, Upload, CalendarX, DollarSign,
  Settings, ShieldCheck, ArrowLeftRight, MessageSquare, Unlink, AlertCircle,
  AlertTriangle, ShieldAlert, CalendarDays, ClipboardList, ArrowRightLeft,
  Receipt, Archive, BookOpen, Database, Ticket, UserPlus, Monitor,
  Layers, Hash, Bell, Lock, FileSpreadsheet, Clipboard, Globe, Share2,
  Home, CreditCard, PieChart, Wallet
} from 'lucide-react';
import { useAuth } from '../App';

const iconMap = {
  LayoutDashboard, Calendar, FileText, Building, Users, Star, BarChart3,
  TrendingUp, HardDrive, Key, UserCheck, Upload, CalendarX, DollarSign,
  Settings, ShieldCheck, ArrowLeftRight, MessageSquare, Unlink, AlertCircle,
  AlertTriangle, ShieldAlert, CalendarDays, ClipboardList, ArrowRightLeft,
  Receipt, Archive, BookOpen, Database, Ticket, UserPlus, Monitor,
  Layers, Hash, Bell, Lock, FileSpreadsheet, Clipboard, Globe, Share2,
  Home, CreditCard, PieChart, ChevronRight, Wallet
};

const IconRenderer = ({ iconName, className = "h-4 w-4" }) => {
  const Icon = iconMap[iconName];
  return Icon ? <Icon className={className} /> : <FileText className={className} />;
};

const MenuCustomizer = () => {
  const { user } = useAuth();
  const [selectedRole, setSelectedRole] = useState('admin');
  const [menuItems, setMenuItems] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Drag state
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [dragType, setDragType] = useState(null); // 'parent' | 'child'

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'bookings_operator', label: 'Operatore Prenotazioni' },
    { value: 'customer_service', label: 'Customer Service' },
    { value: 'invoice_operator', label: 'Operatore Fatture' },
    { value: 'property_responsible', label: 'Responsabile Immobili' },
    { value: 'marketing', label: 'Marketing' }
  ];

  useEffect(() => {
    fetchData();
  }, [selectedRole]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [menuRes, availRes] = await Promise.all([
        api.get(`/menu-config/role/${selectedRole}`),
        api.get(`/menu-config/available-pages/${selectedRole}`).catch(() => ({ data: { available_pages: [] } }))
      ]);
      // Normalize order values to ensure uniqueness (fix duplicate orders from DB)
      const raw = menuRes.data.menu_structure || [];
      const parents = raw.filter(i => !i.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      parents.forEach((p, i) => { p.order = i; });
      // Normalize children per parent
      const parentIds = new Set(parents.map(p => p.id));
      parentIds.forEach(pid => {
        const children = raw.filter(i => i.parent_id === pid).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        children.forEach((c, i) => { c.order = i; });
      });
      setMenuItems(raw);
      setAvailablePages(availRes.data.available_pages || []);
    } catch (error) {
      console.error('Error fetching menu config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  // Get ordered parent items
  const getParentItems = useCallback(() => {
    return menuItems.filter(item => !item.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [menuItems]);

  // Get children for a parent, ordered
  const getChildren = useCallback((parentId) => {
    return menuItems.filter(item => item.parent_id === parentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [menuItems]);

  // Move a parent item up or down among other parents
  const moveParent = useCallback((itemId, direction) => {
    setMenuItems(prev => {
      const parents = prev.filter(i => !i.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = parents.findIndex(p => p.id === itemId);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= parents.length) return prev;
      // Swap orders
      const orderA = parents[idx].order ?? idx;
      const orderB = parents[newIdx].order ?? newIdx;
      return prev.map(item => {
        if (item.id === parents[idx].id) return { ...item, order: orderB };
        if (item.id === parents[newIdx].id) return { ...item, order: orderA };
        return item;
      });
    });
  }, []);

  // Move a child item up or down among siblings
  const moveChild = useCallback((itemId, parentId, direction) => {
    setMenuItems(prev => {
      const siblings = prev.filter(i => i.parent_id === parentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = siblings.findIndex(s => s.id === itemId);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= siblings.length) return prev;
      const orderA = siblings[idx].order ?? idx;
      const orderB = siblings[newIdx].order ?? newIdx;
      return prev.map(item => {
        if (item.id === siblings[idx].id) return { ...item, order: orderB };
        if (item.id === siblings[newIdx].id) return { ...item, order: orderA };
        return item;
      });
    });
  }, []);

  // Reorder parents via drag-and-drop
  const reorderParents = useCallback((fromId, toId) => {
    setMenuItems(prev => {
      const parents = prev.filter(i => !i.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const fromIdx = parents.findIndex(p => p.id === fromId);
      const toIdx = parents.findIndex(p => p.id === toId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      // Remove and insert
      const moved = parents.splice(fromIdx, 1)[0];
      parents.splice(toIdx, 0, moved);
      // Re-assign orders
      const newOrderMap = {};
      parents.forEach((p, i) => { newOrderMap[p.id] = i; });
      return prev.map(item => item.id in newOrderMap ? { ...item, order: newOrderMap[item.id] } : item);
    });
  }, []);

  // Reorder children within same parent via drag-and-drop
  const reorderChildren = useCallback((parentId, fromId, toId) => {
    setMenuItems(prev => {
      const siblings = prev.filter(i => i.parent_id === parentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const fromIdx = siblings.findIndex(s => s.id === fromId);
      const toIdx = siblings.findIndex(s => s.id === toId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const moved = siblings.splice(fromIdx, 1)[0];
      siblings.splice(toIdx, 0, moved);
      const newOrderMap = {};
      siblings.forEach((s, i) => { newOrderMap[s.id] = i; });
      return prev.map(item => item.id in newOrderMap ? { ...item, order: newOrderMap[item.id] } : item);
    });
  }, []);

  const removeItem = useCallback((itemId) => {
    setMenuItems(prev => prev.filter(item => item.id !== itemId && item.parent_id !== itemId));
  }, []);

  const addItem = useCallback((page) => {
    setMenuItems(prev => {
      if (prev.some(i => i.id === page.id)) return prev;
      const maxOrder = prev.filter(i => !i.parent_id).reduce((max, i) => Math.max(max, i.order ?? 0), -1);
      return [...prev, { ...page, order: maxOrder + 1, parent_id: null }];
    });
  }, []);

  const makeChild = useCallback((childId, parentId) => {
    setMenuItems(prev => {
      const parentChildren = prev.filter(i => i.parent_id === parentId);
      const maxOrder = parentChildren.reduce((max, i) => Math.max(max, i.order ?? 0), -1);
      return prev.map(item =>
        item.id === childId ? { ...item, parent_id: parentId, order: maxOrder + 1 } : item
      );
    });
  }, []);

  const makeParent = useCallback((itemId) => {
    setMenuItems(prev => {
      const parents = prev.filter(i => !i.parent_id);
      const maxOrder = parents.reduce((max, i) => Math.max(max, i.order ?? 0), -1);
      return prev.map(item =>
        item.id === itemId ? { ...item, parent_id: null, order: maxOrder + 1 } : item
      );
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/menu-config/role/${selectedRole}`, { menu_structure: menuItems });
      alert('Configurazione menu salvata!');
    } catch (error) {
      alert('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Ripristinare la configurazione predefinita? Tutte le personalizzazioni per questo ruolo verranno perse.')) return;
    try {
      await api.delete(`/menu-config/role/${selectedRole}`);
      await fetchData();
      alert('Configurazione ripristinata!');
    } catch (error) {
      alert('Errore nel ripristino');
    }
  };

  // Drag handlers for parent items
  const handleDragStart = (e, itemId, type, parentId) => {
    setDragItem(itemId);
    setDragType(type);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ itemId, type, parentId }));
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragItem(null);
    setDragOverItem(null);
    setDragType(null);
  };

  const handleDragOver = (e, itemId, type) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragType === type) {
      setDragOverItem(itemId);
    }
  };

  const handleDrop = (e, targetId, type, targetParentId) => {
    e.preventDefault();
    setDragOverItem(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type !== type) return;
      if (data.itemId === targetId) return;
      if (type === 'parent') {
        reorderParents(data.itemId, targetId);
      } else if (type === 'child' && data.parentId === targetParentId) {
        reorderChildren(targetParentId, data.itemId, targetId);
      }
    } catch (err) { /* ignore */ }
  };

  const activeIds = new Set(menuItems.map(i => i.id));
  const notInMenu = availablePages.filter(p => !activeIds.has(p.id));
  const filteredNotInMenu = notInMenu.filter(p =>
    !searchTerm || p.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const parentItems = getParentItems();

  return (
    <Layout title="Personalizza Menu">
      <div className="space-y-4" data-testid="menu-customizer-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Personalizza Menu per Ruolo</h2>
            <p className="text-sm text-slate-500 mt-1">
              Trascina le voci per riordinarle. Le modifiche si applicano a tutti gli utenti con questo ruolo.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="role-selector"
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Caricamento...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Active Menu */}
            <div className="lg:col-span-2 space-y-3">
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">
                    Menu Attivo ({parentItems.length} voci principali)
                  </h3>
                  <div className="flex gap-2">
                    <Button onClick={handleReset} variant="outline" size="sm">
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Ripristina Default
                    </Button>
                    <Button onClick={handleSave} disabled={saving} size="sm"
                      className="bg-blue-600 hover:bg-blue-700">
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-1" data-testid="active-menu-list">
                  {parentItems.map((item, idx) => {
                    const children = getChildren(item.id);
                    const isExpanded = expandedItems.includes(item.id);
                    const prevParent = idx > 0 ? parentItems[idx - 1] : null;
                    const isDragOver = dragOverItem === item.id && dragType === 'parent';

                    return (
                      <div key={item.id}>
                        {/* Parent row */}
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, item.id, 'parent')}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, item.id, 'parent')}
                          onDrop={(e) => handleDrop(e, item.id, 'parent')}
                          className={`flex items-center gap-2 p-2 border rounded-lg group transition-all ${
                            dragItem === item.id ? 'opacity-40' : ''
                          } ${isDragOver ? 'bg-blue-50 border-blue-400 border-dashed' : 'bg-white hover:bg-slate-50'}`}
                          data-testid={`menu-item-${item.id}`}
                        >
                          <GripVertical className="h-4 w-4 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" />
                          
                          {children.length > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }} className="text-slate-400 hover:text-slate-600 shrink-0">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                          
                          <IconRenderer iconName={item.icon} className="h-4 w-4 text-slate-500 shrink-0" />
                          <span className="flex-1 text-sm font-medium text-slate-800 truncate">{item.label}</span>
                          <span className="text-xs text-slate-400 font-mono hidden sm:inline">{item.path}</span>
                          
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <button onClick={() => moveParent(item.id, -1)} disabled={idx === 0}
                              className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30"
                              title="Sposta su">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => moveParent(item.id, 1)} disabled={idx >= parentItems.length - 1}
                              className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30"
                              title="Sposta giu">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                            {prevParent && (
                              <button onClick={() => {
                                makeChild(item.id, prevParent.id);
                                if (!expandedItems.includes(prevParent.id)) {
                                  setExpandedItems(prev => [...prev, prevParent.id]);
                                }
                              }}
                                className="p-1 text-slate-400 hover:text-purple-600"
                                title={`Sposta sotto "${prevParent.label}"`}>
                                <ChevronRight className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button onClick={() => removeItem(item.id)}
                              className="p-1 text-slate-400 hover:text-red-600" title="Rimuovi dal menu">
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Children */}
                        {(isExpanded || children.length > 0) && children.map((child, cIdx) => {
                          const isDragOverChild = dragOverItem === child.id && dragType === 'child';
                          return (
                            <div key={child.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, child.id, 'child', item.id)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(e, child.id, 'child')}
                              onDrop={(e) => handleDrop(e, child.id, 'child', item.id)}
                              className={`flex items-center gap-2 p-2 pl-10 border border-l-4 border-l-blue-300 rounded-lg ml-4 mt-1 group transition-all ${
                                dragItem === child.id ? 'opacity-40' : ''
                              } ${isDragOverChild ? 'bg-blue-50 border-blue-400 border-dashed' : 'bg-slate-50 hover:bg-blue-50'}`}
                              data-testid={`menu-item-${child.id}`}
                            >
                              <GripVertical className="h-3.5 w-3.5 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" />
                              <IconRenderer iconName={child.icon} className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <span className="flex-1 text-sm text-slate-700 truncate">{child.label}</span>
                              <span className="text-xs text-slate-400 font-mono hidden sm:inline">{child.path}</span>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <button onClick={() => moveChild(child.id, item.id, -1)} disabled={cIdx === 0}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30" title="Sposta su">
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => moveChild(child.id, item.id, 1)} disabled={cIdx >= children.length - 1}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30" title="Sposta giu">
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => makeParent(child.id)}
                                  className="p-1 text-slate-400 hover:text-orange-600 text-xs" title="Rendi voce principale">
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => removeItem(child.id)}
                                  className="p-1 text-slate-400 hover:text-red-600" title="Rimuovi">
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {parentItems.length === 0 && (
                    <p className="text-center py-6 text-slate-400 text-sm">
                      Nessuna voce nel menu. Aggiungi pagine dal pannello a destra.
                    </p>
                  )}
                </div>
              </Card>
            </div>

            {/* Available Pages */}
            <div className="space-y-3">
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">
                    Pagine Disponibili
                  </h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {notInMenu.length} non nel menu
                  </span>
                </div>

                {notInMenu.length > 5 && (
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Cerca pagina..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                      data-testid="page-search"
                    />
                  </div>
                )}

                <div className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="available-pages-list">
                  {filteredNotInMenu.map(page => (
                    <div key={page.id}
                      className="flex items-center gap-2 p-2 bg-slate-50 border border-dashed rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors cursor-pointer"
                      onClick={() => addItem(page)}
                      data-testid={`available-page-${page.id}`}>
                      <Plus className="h-3.5 w-3.5 text-green-500" />
                      <IconRenderer iconName={page.icon} className="h-3.5 w-3.5 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{page.label}</p>
                        <p className="text-xs text-slate-400 truncate">{page.path}</p>
                      </div>
                    </div>
                  ))}
                  {filteredNotInMenu.length === 0 && notInMenu.length > 0 && (
                    <p className="text-center py-4 text-slate-400 text-sm">Nessun risultato</p>
                  )}
                  {notInMenu.length === 0 && (
                    <p className="text-center py-4 text-green-600 text-sm font-medium">
                      Tutte le pagine sono nel menu
                    </p>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h4 className="font-semibold text-slate-700 text-sm mb-2">Azioni Rapide</h4>
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start text-xs"
                    onClick={() => { notInMenu.forEach(page => addItem(page)); }}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi tutte le pagine mancanti
                  </Button>
                </div>
              </Card>

              <Card className="p-3 bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Suggerimento:</strong> Trascina <GripVertical className="h-3 w-3 inline" /> per riordinare le voci.
                  Usa le frecce per spostare su/giu.
                  Usa <ChevronRight className="h-3 w-3 inline" /> per nidificare una pagina sotto la precedente.
                  Ricorda di <strong>Salvare</strong> dopo le modifiche.
                </p>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MenuCustomizer;
